/**
 * Type definitions for PCA Orientation Analysis Tool
 * Provides types for element orientation analysis using Principal Component Analysis
 */

// Ensure THREE is available globally
declare const THREE: any;

/**
 * Result of PCA orientation analysis
 */
export interface OrientationResult {
  /** The geometric center of the element's 2D projection */
  center: THREE.Vector3;
  
  /** The normalized direction vector of the element's longest axis */
  direction: THREE.Vector3;
  
  /** Database ID of the analyzed element */
  dbId: number;
  
  /** Confidence score (0-1) based on eigenvalue ratio */
  confidence: number;
  
  /** Eigenvalues from PCA (for debugging/validation) */
  eigenvalues: {
    primary: number;
    secondary: number;
  };
  
  /** Element name for display */
  elementName?: string;
  
  /** Analysis timestamp */
  timestamp: Date;
}

/**
 * Configuration options for PCA analysis
 */
export interface PCAConfig {
  /** Maximum vertices to process (for performance) */
  maxVertices?: number;
  
  /** Minimum confidence threshold for valid results */
  minConfidence?: number;
  
  /** Arrow visualization length in model units */
  arrowLength?: number;
  
  /** Arrow color (hex) */
  arrowColor?: string;
  
  /** Z-offset for arrow placement above element */
  arrowZOffset?: number;
  
  /** Enable vertex sampling for large meshes */
  enableSampling?: boolean;
  
  /** Sample every Nth vertex when sampling is enabled */
  sampleRate?: number;
}

/**
 * Visualization state for an analyzed element
 */
export interface OrientationVisualization {
  /** Analysis result */
  result: OrientationResult;
  
  /** Unique ID for the arrow overlay */
  arrowId: string;
  
  /** Saved camera state for "View" button */
  viewState?: {
    position: THREE.Vector3;
    target: THREE.Vector3;
    up: THREE.Vector3;
    fov: number;
  };
  
  /** Is arrow currently visible */
  isVisible: boolean;
  
  /** Arrow mesh reference for cleanup */
  arrowMesh?: THREE.ArrowHelper;
}

/**
 * UI state for the orientation tool
 */
export interface OrientationToolState {
  /** Current mode */
  mode: 'idle' | 'selecting' | 'analyzing' | 'results';
  
  /** Analysis results history */
  results: OrientationVisualization[];
  
  /** Currently selected result */
  selectedResult?: OrientationVisualization;
  
  /** Loading/processing state */
  isProcessing: boolean;
  
  /** Error message if any */
  error?: string;
  
  /** Current configuration */
  config: PCAConfig;
}

/**
 * 2D Point for PCA calculation
 */
export interface Point2D {
  x: number;
  y: number;
}

/**
 * Covariance matrix (2x2)
 */
export interface CovarianceMatrix {
  xx: number;
  xy: number;
  yx: number;
  yy: number;
}

/**
 * Eigenvector/eigenvalue pair
 */
export interface EigenPair {
  value: number;
  vector: {
    x: number;
    y: number;
  };
}

/**
 * Vertex extraction result
 */
export interface VertexExtractionResult {
  /** Extracted vertices in world coordinates */
  vertices: THREE.Vector3[];
  
  /** Total vertices before sampling */
  totalVertices: number;
  
  /** Whether sampling was applied */
  wasSampled: boolean;
  
  /** Bounding box of the element */
  boundingBox: THREE.Box3;
}

/**
 * Default configuration values
 */
export const DEFAULT_PCA_CONFIG: PCAConfig = {
  maxVertices: 5000,
  minConfidence: 0.3,
  arrowLength: 5,
  arrowColor: '#FF0000',
  arrowZOffset: 0.5,
  enableSampling: true,
  sampleRate: 10
};

/**
 * Analysis status messages
 */
export enum AnalysisStatus {
  IDLE = 'Select an element to analyze',
  SELECTING = 'Click on an element in the model',
  ANALYZING = 'Analyzing element orientation...',
  SUCCESS = 'Analysis complete',
  NO_GEOMETRY = 'Element has no geometry',
  SYMMETRIC = 'Element is symmetric (no clear primary axis)',
  ERROR = 'Analysis failed'
}